// BeaconDot.tsx — the blinking status dot used everywhere in Sheet Beacons.
//
//   live      green, soft ripple every 2s ("I'm alive")
//   new       lime, fast double ripple ("new leads arrived")
//   checking  blue, spinning ring (a check is running)
//   error     red, hard blink
//   paused    grey, still
//
// The keyframes are injected into <head> once, so the dot works anywhere
// (tree, history table, Leads header…) without extra setup.

import React, { useEffect } from 'react';
import { HEALTH, Health } from './beaconTypes';

const STYLE_ID = 'beacon-dot-css';

export const BEACON_CSS = `
.bcn-dot { position: relative; display: inline-block; border-radius: 50%; flex-shrink: 0; }
.bcn-dot::before, .bcn-dot::after { content: ''; position: absolute; inset: 0; border-radius: 50%; background: inherit; opacity: 0; pointer-events: none; }
.bcn-live::before { animation: bcnRipple 2.2s ease-out infinite; }
.bcn-new::before { animation: bcnRipple 1.1s ease-out infinite; }
.bcn-new::after { animation: bcnRipple 1.1s ease-out 0.35s infinite; }
.bcn-idle::before { animation: bcnRipple 2.8s ease-out infinite; }
.bcn-error { animation: bcnBlink 0.9s steps(2, jump-none) infinite; }
.bcn-checking { background: transparent !important; border: 2px solid rgba(56,189,248,0.25); border-top-color: #38bdf8; animation: bcnSpin 0.8s linear infinite; }
@keyframes bcnRipple { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(3.2); opacity: 0; } }
@keyframes bcnBlink { 0% { opacity: 1; } 100% { opacity: 0.25; } }
@keyframes bcnSpin { to { transform: rotate(360deg); } }
/* still: same colour, no motion — used by the legend so only real beacons blink */
.bcn-still, .bcn-still::before, .bcn-still::after { animation: none !important; }
@media (prefers-reduced-motion: reduce) {
  .bcn-dot, .bcn-dot::before, .bcn-dot::after { animation: none !important; }
}
`;

export const useBeaconCss = () => {
  useEffect(() => {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = BEACON_CSS;
    document.head.appendChild(el);
  }, []);
};

interface BeaconDotProps {
  health: Health;
  size?: number;
  title?: string;
  /** Show the colour only, without blinking (e.g. in a legend). */
  still?: boolean;
}

const BeaconDot: React.FC<BeaconDotProps> = ({ health, size = 10, title, still = false }) => {
  useBeaconCss();
  const h = HEALTH[health];
  return (
    <span
      className={`bcn-dot bcn-${health}${still ? ' bcn-still' : ''}`}
      title={title ?? h.label}
      aria-label={title ?? h.label}
      style={{
        width: size,
        height: size,
        background: h.color,
        boxShadow: health === 'paused' || health === 'checking' ? 'none' : `0 0 ${Math.round(size * 0.9)}px ${h.glow}`,
      }}
    />
  );
};

export default BeaconDot;
